import { Injectable, OnDestroy, OnInit } from "@angular/core";
import {
  BiometryType,
  type AuthenticateOptions, BiometryError,
  type CheckBiometryResult,
  BiometryErrorType,
  BiometricAuth,
  getBiometryName
} from '@aparajita/capacitor-biometric-auth';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { ToastController } from "@ionic/angular";

@Injectable({
  providedIn: 'root',
})
export class BiometricService implements OnDestroy, OnInit {
  constructor(
    public toastController: ToastController,
  ) {
    this.ngOnInit();
  }

  ngOnDestroy(): void {
    this.appListener?.remove();
  }

  hideBio: boolean = false;

  biometryTypes = [
    {
      title: 'None',
      type: BiometryType.none,
    },
    {
      title: 'Touch ID',
      type: BiometryType.touchId,
    },
    {
      title: 'Face ID',
      type: BiometryType.faceId,
    },
    {
      title: 'Fingerprint Authentication',
      type: BiometryType.fingerprintAuthentication,
    },
    {
      title: 'Face Authentication',
      type: BiometryType.faceAuthentication,
    },
    {
      title: 'Iris Authentication',
      type: BiometryType.irisAuthentication,
    },
  ]

  biometry = <CheckBiometryResult>({
    isAvailable: false,
    biometryType: BiometryType.none,
    biometryTypes: [BiometryType.none],
    reason: '',
    code: BiometryErrorType.none,
  });

  options = <AuthenticateOptions>({
    reason: 'Please authenticate',
    cancelTitle: 'Cancel',
    iosFallbackTitle: 'Use passcode',
    androidTitle: 'Biometric login',
    androidSubtitle: 'Use biometric authentication',
    allowDeviceCredential: true,
    androidConfirmationRequired: false,
  })

  biometryType = String(BiometryType.none);
  appListener?: PluginListenerHandle;

  get isNative() {
    return Capacitor.isNativePlatform();
  }
  get isIOS() {
    return Capacitor.getPlatform() === 'ios';
  }
  get isAndroid() {
    return Capacitor.getPlatform() === 'android';
  }

  biometryName() {
    if (this.isNative) {
      if (this.biometry.biometryTypes.length === 0) {
        return 'No biometry';
      }

      if (this.biometry.biometryTypes.length === 1) {
        return getBiometryName(this.biometry.biometryType);
      }

      return 'Biometry';
    } else {
      return getBiometryName(this.biometry.biometryType) || 'No biometry';
    }
  }

  //Device's biometric description
  //TODO Uncoment when there is any need in biomrteicType selector
  /* biometryDescription(): string {
    let description: string;

    if (this.isNative) {
      if (this.biometry.biometryTypes.length > 0) {
        description = `${this.biometry.biometryTypes
          .map((type) => getBiometryName(type))
          .join(' and ')} ${this.biometry.biometryTypes.length === 1 ? 'is' : 'are'
          } supported`
      } else {
        description = 'No biometry is supported';
      }
    } else {
      description = `${this.biometryName} is supported`;
    }

    if (this.biometry.biometryType !== BiometryType.none) {
      if (this.biometry.isAvailable) {
        description += ` and ${this.biometry.biometryTypes.length > 1 ? 'potentially ' : ''
          }available.`;
      } else {
        description += ', but not available.';
      }

      if (this.biometry.reason) {
        description += ` ${this.biometry.reason} (${this.biometry.code})`;
      }
    } else {
      description += '.';
    }

    return description;
  }*/

  updateBiometryInfo(info: CheckBiometryResult): void {
    this.biometry = info;
  }

  async ngOnInit() {
    let ch = await BiometricAuth.checkBiometry();
    this.updateBiometryInfo(ch);

    try {
      this.appListener = await BiometricAuth.addResumeListener(this.updateBiometryInfo);
    } catch (error) {
      if (error instanceof Error) {
        console.error(error.message);
      }
    }
  }

  async showAlert(message: string): Promise<void> {
    const alert = await this.toastController.create({
      message,
      duration: 5000,
      layout: "baseline",
      cssClass: 'custom-toast',
      position: 'bottom',
    });
    await alert.present();
  }

  async showErrorAlert(error: BiometryError): Promise<void> {
    await this.showAlert(`${error.message}.`);
  }

  async onAuthenticate(): Promise<void> {
    let ch = await BiometricAuth.checkBiometry();
    if (!ch.isAvailable) return;

    // Drop the soft keyboard before showing the native biometric prompt.
    // On Android the keyboard shrinks the WebView (adjustResize); if it is
    // still up when the biometric prompt dismisses the surrounding modal, the
    // WebView fails to restore to full height and the bottom of the screen
    // stays black. Covers the case where the user focused a field (e.g. tapped
    // the PIN input) before invoking biometrics.
    if (this.isNative) {
      (document.activeElement as HTMLElement | null)?.blur?.();
      try {
        await Keyboard.hide();
      } catch {
        // Keyboard may not be open / plugin unavailable — safe to ignore.
      }
    }

    return await BiometricAuth.authenticate(this.options);
  }

  //TODO: Add biometry selector if needed
  async onSelectBiometry(value: number): Promise<void> {
    await BiometricAuth.setBiometryType(value);
    this.updateBiometryInfo(await BiometricAuth.checkBiometry());
  }
}
